<script lang="ts">
  import { onMount } from "svelte";
  import { InventoryItemStore } from "@retailer/svelte/CartWidget/v2/Stores/InventoryItemStore";
  import type { Product, ProductLine } from "@shared/types/ProductLine";
  import NewDeals from "./NewDeals.svelte";
  import Pricing from "./Pricing.svelte";
  import ProductInfoLine from "./ProductInfoLine.svelte";
  import FuturePrices from "@retailer/svelte/ProductDetails/Components/FuturePrices.svelte";
  import PostOffPricing from "@retailer/svelte/ProductDetails/Components/PostOffPricing.svelte";
  import Authorization from "@shared/svelte/Components/Authorization.svelte";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { get as getStoreValue } from "svelte/store";
  import Skeleton from "svelte-skeleton/Skeleton.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import {
    getDataForInventory,
    clearCacheForInventory,
  } from "@retailer/services/pricing_data_service";
  import { widgetItemIsRequestable, widgetItemHasBeenRequested } from "../ProductHelpers.svelte";
  import PickupOrDeliveryOutsideCart from "@retailer/svelte/Shared/PickupOrDeliveryOutsideCart.svelte";
  import ProductPageLastOrdered from "./ProductPageLastOrdered.svelte";
  import type { Distributor } from "@shared/types/Distributor";
  import ProductDetailsCartWidgetContainer from "@retailer/svelte/CartWidget/v2/Containers/ProductDetailsCartWidgetContainer.svelte";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { hideFulfillmentMethodPickerForDistributor } from "@retailer/services/fulfillment_methods_service";

  export let product: Product;
  export let productLine: ProductLine;
  export let distributor: Distributor;
  export let callbacks: any;
  export let deleteCustomPrice: any;
  export let customPricingMode: boolean = false;
  export let priceEditUnit: { [key: number]: number } = {};
  export let priceEditCase: { [key: number]: number } = {};
  export let priceSubmitPending: boolean;
  export let Cart;
  export let isPricingLoading: boolean = false;

  let currentUser: any = getStoreValue(CurrentUserStore);
  let loaded: boolean = false;

  onMount(async () => {
    loaded = false;
    const response = await InventoryItemStore.fetchInventoryItem(product.inventory_id);
    loaded = response?.loaded;
  });

  const stateSettings = getStoreValue(StateSettingsStore);
  const usesFuturePricingWithMinBottle = stateSettings
    ? stateSettings["uses_future_pricing_with_min_bottle"]
    : false;
  const usesFuturePricing = stateSettings ? stateSettings["uses_future_pricing"] : false;

  const updatePricesErrorCb = (_err: any) => {
    product.unit_price = null;
    product.case_price = null;
  };

  const updatePrices = async () => {
    if (distributor?.uses_warehouse_pickup) {
      let inventoryIdString = String(product.inventory_id);
      clearCacheForInventory(inventoryIdString);
      let pricingData = await getDataForInventory(inventoryIdString, updatePricesErrorCb);
      let price = pricingData?.inventory_price;
      if (price) {
        ["unit_price", "case_price", "discounted_unit_price", "discounted_case_price"].forEach(
          prop => (product[prop] = price[prop]),
        );
      }
    }
  };

  $: widgetItem = $InventoryItemStore?.inventoryItems[product.inventory_id];

  $: productIsRequestable = widgetItemIsRequestable(currentUser, widgetItem);
  $: productHasBeenRequested = widgetItemHasBeenRequested(currentUser, widgetItem);
  $: showPostOffPricing = showFuturePricing && usesFuturePricingWithMinBottle;
  $: showFuturePricing =
    distributor?.seven_fifty_integration && (usesFuturePricing || usesFuturePricingWithMinBottle);
  $: distributorUsesHappyHour = widgetItem?.distributor_summary.uses_happy_hour;
  $: hasHappyHourPrices = widgetItem?.has_hh_prices;
  $: showPricing =
    productIsRequestable ||
    (!distributorUsesHappyHour && widgetItem?.buying_options?.length) ||
    (distributorUsesHappyHour && hasHappyHourPrices);
</script>

<div
  class="padding--small product-detail__product-row"
  data-product-id={product.id}
  data-testid={`productBox-${product.id}`}
>
  <div class="grid-row align--middle">
    <ProductInfoLine {product} {productLine} {customPricingMode} {productIsRequestable} />

    <NewDeals {product} {callbacks} {usesFuturePricingWithMinBottle} {productIsRequestable} />

    <div class="fizz-styles grid-col c12-12">
      {#if isPricingLoading}
        <div
          class="fizz-flow-8 fizz-stack-4 pricing-loader"
          data-testid="pricing-loading-{product.id}"
        >
          <Skeleton animate={true} speed={0.5} height={20} width={240} />
          <Skeleton animate={true} speed={0.5} height={20} width={240} />
        </div>
      {:else if showPricing}
        <Authorization authName={"products.show_prices"}>
          <div
            class={showPostOffPricing
              ? "fizz-stack-16"
              : "fizz-flex-row-responsive fizz-space-between-responsive fizz-align-content-start fizz-stack-16 margin-top--smallest"}
          >
            {#if showPostOffPricing}
              <PostOffPricing {product} {productLine} />
            {:else}
              <Pricing
                {customPricingMode}
                {productLine}
                showListPrice={distributor?.seven_fifty_integration}
                {product}
                {deleteCustomPrice}
                bind:priceEditUnit
                bind:priceEditCase
                {priceSubmitPending}
              />
            {/if}
            {#if showFuturePricing}
              <div>
                <FuturePrices {product} {productLine} />
              </div>
            {/if}
          </div>
        </Authorization>
      {/if}
    </div>
  </div>

  {#if !customPricingMode}
    {#if !product.inventory_is_completely_out_of_stock && !stagedRolloutCheck("marketplace__fulfillment_methods") && !hideFulfillmentMethodPickerForDistributor(distributor?.id)}
      <PickupOrDeliveryOutsideCart
        {product}
        {Cart}
        {distributor}
        callback={() => {
          updatePrices();
        }}
      />
    {/if}
    <ProductPageLastOrdered {product} {distributor} />

    <ProductDetailsCartWidgetContainer
      inventoryId={product.inventory_id}
      {product}
      {distributor}
      {loaded}
      on:openDrawer
    />
  {/if}
  {#if productHasBeenRequested}
    <div class="media-fixed default-small--normal s6 align-right">
      {#if currentUser.group_director}
        <span>
          This product has been requested.
          <UIRouterLink
            stateName={"layout.limited_ordering_products"}
            classes="underline-hover cursor-pointer color-brandPrimary strong"
            >Manage Request.</UIRouterLink
          >
        </span>
      {:else}
        <span class="approval-request-status">
          Item requested.<br />
          <UIRouterLink
            stateName={"layout.quick_order_list_index"}
            classes="underline-hover cursor-pointer color-brandPrimary strong"
            >View Status</UIRouterLink
          >
        </span>
      {/if}
    </div>
  {/if}
</div>
